import * as d3 from 'd3'
import emptyThumb from '../../assets/images/emptydata.svg'
import './styles.css'
import Chart from '../../ultils/Chart'
import d3tip from '../../ultils/d3-tip'
import moment from 'moment'
import copyThumb from '../../assets/images/copy-new.svg'
import checkedCopy from '../../assets/images/checkedCopy.svg'
class FocusChart extends Chart {
  init({
    data,
    events,
    tickerId,
    animateInitialDraw,
    triggerZoom,
    registerFocusChartTimeChange,
    currency,
  }) {
    let vis = this
    let tooltipTimeout = null
    vis.data = data
    vis.events = events
    vis.tickerId = tickerId
    vis.enableInitialDrawAnimation = Boolean(animateInitialDraw)
    vis.animatedTickerKey = null
    vis.isAnimatingInitialDraw = false
    vis.currency = currency
    vis.marketContextToggleObj = {
      showRiskEvents: false,
      showUnrecognisedRiskStatus: true,
      showNoteHistory: false,
      showRiskOutlookWatchlist: true,
      showRiskSmoke: false,
      showRiskFire: false,
    }
    vis.triggerZoom = triggerZoom

    vis.tip = d3tip()
      .attr('class', 'd3-tip-focus-chart')
      .html((e, d) => {
        return `
        <div class="tip-content wide-tooltip">
          <div class="tip-header">
            <p class="tip-date">${moment(d.date).format('MMM DD, YYYY')}</p>
            <p class="tip-price">${d.title}</p>
          </div>
        </div>
      `
      })
    vis.g.call(vis.tip)

    vis.tooltipLine1 = vis.g.append('line')
    vis.tooltipLine2 = vis.g.append('line')

    vis.clipId = `focus-chart-clip-${Math.random().toString(36).slice(2, 9)}`
    vis.clipRect = vis.svg
      .append('defs')
      .append('clipPath')
      .attr('id', vis.clipId)
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)

    vis.lg = vis.svg
      .append('defs')
      .append('linearGradient')
      .attr('id', 'mygrad')
      .attr('x1', '0%')
      .attr('x2', '0%')
      .attr('y1', '0%')
      .attr('y2', '100%')
    vis.lg
      .append('stop')
      .attr('offset', '0%')
      .style('stop-color', 'rgba(51, 147, 243)')
      .style('stop-opacity', 0.4)

    vis.lg
      .append('stop')
      .attr('offset', '100%')
      .style('stop-color', 'rgba(51, 147, 243)')
      .style('stop-opacity', 0)

    vis.g
      .append('path')
      .attr('class', 'line')
      .attr('fill', 'none')
      .attr('stroke', '#446DA6')
      .attr('stroke-width', '2px')
      .attr('stroke-opacity', 1)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .attr('clip-path', `url(#${vis.clipId})`)

    vis.g
      .append('path')
      .attr('class', 'area')
      .attr('clip-path', `url(#${vis.clipId})`)

    vis.x = d3.scaleTime()
    vis.y = d3.scaleLinear()

    vis.xAxisCall = d3.axisBottom().ticks(7).tickFormat(d3.timeFormat('%b %Y'))
    vis.yAxisCall = d3.axisLeft().ticks(6)

    vis.xAxis = vis.g
      .append('g')
      .attr('class', 'xAxisFocusChart')
      .attr('transform', `translate(0, ${vis.HEIGHT})`)
    vis.yAxis = vis.g.append('g').attr('class', 'yAxisFocusChart')
    vis.yGrid = vis.g.append('g').attr('class', 'yGridFocusChart')

    vis.zoom = d3.zoom().scaleExtent([1, Infinity]).on('zoom', vis.triggerZoom)

    vis.zoomRect = vis.g.append('rect').attr('class', 'zoom')

    vis.line = d3
      .line()
      .curve(d3.curveCatmullRom)
      .defined(d => !isNaN(d.Price) || !isNaN(d.date))
      .x(d => vis.x(d.date))
      .y(d => vis.y(d.Price))

    vis.area = d3
      .area()
      .curve(d3.curveCatmullRom)
      .x(d => vis.x(d.date))
      .y0(vis.HEIGHT)
      .y1(d => vis.y(d.Price) + 10)

    registerFocusChartTimeChange(vis)

    vis.minText = vis.g
      .append('text')
      .attr('x', 10)
      .attr('y', vis.HEIGHT - 20)
      .attr('class', 'minMaxText')

    vis.maxText = vis.g
      .append('text')
      .attr('x', 10)
      .attr('y', 0)
      .attr('class', 'minMaxText')

    vis.update({ data, events })
  }

  getTickerAnimationKey() {
    const vis = this
    return vis.tickerId || '__default_ticker__'
  }

  getInitialDrawDuration() {
    const vis = this
    const points = vis?.data?.length ?? 0
    return Math.max(1800, Math.min(2800, points * 16))
  }

  shouldAnimateInitialDraw(onZoom) {
    const vis = this
    if (!vis.enableInitialDrawAnimation) return false
    if (onZoom || vis.isAnimatingInitialDraw) return false
    if (!Array.isArray(vis.data) || vis.data.length <= 1) return false

    const tickerKey = vis.getTickerAnimationKey()
    return vis.animatedTickerKey !== tickerKey
  }

  runInitialDrawAnimation() {
    const vis = this
    const tickerKey = vis.getTickerAnimationKey()
    vis.animatedTickerKey = tickerKey
    vis.isAnimatingInitialDraw = true

    vis.clipRect.interrupt().attr('width', 0)
    vis.clipRect
      .transition()
      .duration(vis.getInitialDrawDuration())
      .ease(d3.easeCubicOut)
      .attr('width', vis.WIDTH)
      .on('end', () => {
        vis.isAnimatingInitialDraw = false
      })
      .on('interrupt', () => {
        vis.isAnimatingInitialDraw = false
      })
  }

  ensureChartFullyVisible() {
    const vis = this
    if (vis.isAnimatingInitialDraw) return
    vis.clipRect.interrupt().attr('width', vis.WIDTH)
  }

  updateStyleForXAsix() {
    const vis = this
    vis.xAxis
      .select('.domain')
      .attr('stroke', '#E6EBF2')
      .attr('stroke-width', '1')

    vis.xAxis
      .selectAll('.tick line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 6)
      .attr('stroke', 'transparent')

    vis.xAxis
      .selectAll('.tick text')
      .attr('class', 'tickTextFocusChart')
      .attr('fill', '#535862')
  }

  update({ data, events, onZoom, tickerId }) {
    let vis = this
    vis.data = data || vis.data
    vis.events = events || vis.events
    vis.tickerId = tickerId || vis.tickerId
    vis.svg.selectAll('image').remove()

    if (vis.data.length > 1) {
      if (!onZoom) {
        vis.svg.selectAll('image').remove()
        if (d3.min(d3.map(vis.data, d => d.Price))) {
          vis.minText.text(
            `Min: ${vis?.currency} ${d3.min(d3.map(vis.data, d => d.Price))}`,
          )
        } else {
          vis.minText.text(null)
        }
        if (d3.max(d3.map(vis.data, d => d.Price))) {
          vis.maxText.text(
            `Max: ${vis?.currency} ${d3.max(d3.map(vis.data, d => d.Price))}`,
          )
        } else {
          vis.maxText.text(null)
        }
      }
    } else {
      vis.data = []
      vis.events = []
      vis.svg
        .append('svg:image')
        .attr('xlink:href', emptyThumb)
        .attr('x', vis.WIDTH / 2 - 77 + vis.MARGIN.LEFT)
        .attr('y', vis.HEIGHT / 2 - 37 + vis.MARGIN.TOP)
        .attr('width', '155')
        .attr('height', '73')
      vis.minText.text('')
      vis.maxText.text('')
      vis.g.selectAll('.tick').remove()
    }
    vis.updateStyleForXAsix()

    vis.clipRect.attr('height', vis.HEIGHT)
    vis.xAxis.attr('transform', `translate(0, ${vis.HEIGHT})`)

    vis.zoom
      .translateExtent([
        [0, 0],
        [vis.WIDTH, vis.HEIGHT],
      ])
      .extent([
        [0, 0],
        [vis.WIDTH, vis.HEIGHT],
      ])

    vis.zoomRect
      .attr('width', vis.WIDTH)
      .attr('height', vis.HEIGHT)
      .call(vis.zoom)

    if (!onZoom) {
      vis.x.range([0, vis.WIDTH]).domain(d3.extent(vis.data, d => d.date))
      vis.y
        .range([vis.HEIGHT - 50, 0])
        .domain([
          d3.min(vis.data, d => Number(d.Price)) / 1.05,
          d3.max(vis.data, d => Number(d.Price)) * 1.05,
        ])
    }

    vis.xAxisCall.scale(vis.x)
    vis.xAxis.call(vis.xAxisCall)
    vis.yAxisCall.scale(vis.y)
    vis.yAxis.call(vis.yAxisCall)
    vis.yGrid
      .call(d3.axisLeft(vis.y).ticks(5).tickSize(-vis.WIDTH).tickFormat(''))
      .select('.domain')
      .remove()

    vis.g.select('.line').attr('d', vis.line(vis.data))

    vis.g.select('.line').raise()

    vis.g
      .select('.area')
      .attr('class', 'area')
      .attr('d', vis.area(vis.data))
      .style('fill', 'rgba(66, 108, 166, 0.03)')

    const shouldAnimate = vis.shouldAnimateInitialDraw(onZoom)
    if (shouldAnimate) {
      vis.runInitialDrawAnimation()
    } else {
      vis.ensureChartFullyVisible()
    }

    if (!onZoom) {
      vis.x.domain(d3.extent(vis.data, d => d.date))
      vis.y.domain([
        d3.min(vis.data, d => Number(d.Price)) / 1.05,
        d3.max(vis.data, d => Number(d.Price)) * 1.05,
      ])
    }

    vis.addEventCircles()
  }

  filterByMarketContextToggle(d) {
    const vis = this
    if (!vis.marketContextToggleObj) {
      return true
    }
    if (vis.marketContextToggleObj.showNoteHistory) {
      return true
    }
    return vis.isRiskEvent(d)
      ? vis.marketContextToggleObj.showRiskEvents
      : vis.marketContextToggleObj.showUnrecognisedRiskStatus
  }

  getEventColor(d) {
    // if (!d['signal_data_array'] || d['signal_data_array'].length === 0) {
    //   return '#1D3967'
    // }
    const riskLevel = d['risk_outlook'] ?? null
    const colorEventScale = d3
      .scaleOrdinal()
      .domain([null, 'Watchlist', 'Smoke', 'Fire'])
      .range(['#1D3967', '#E39193', '#ED5F5F', '#DE1313'])
    return colorEventScale(riskLevel)
  }

  isRiskEvent(d) {
    return d['signal_type'] === 'Risk Story'
  }

  isRiskStatus(d) {
    return d['signal_type'] && d['signal_type'] === 'Risk Outlook'
  }

  countCyValue(d) {
    const vis = this
    const bisect = d3.bisector(d => d.date).left
    const i = bisect(vis.data, d.date)
    const val = vis.data[i]

    if (!val) return null

    function findOffsetRecursive(index, offset) {
      if (index <= 0) return offset
      const da = vis.events[index - 1]
      if (
        (da && da.Date !== d.Date) ||
        (da && vis.isRiskEvent(da) !== vis.isRiskEvent(d))
      )
        return offset
      return findOffsetRecursive(index - 1, offset + 10)
    }

    const offset = findOffsetRecursive(d.id, 0)

    return vis.y(val.Price) + offset
  }

  removeTooltip() {
    const vis = this
    if (vis.tooltipLine1) vis.tooltipLine1.attr('stroke', 'none')
    if (vis.tooltipLine2) vis.tooltipLine2.attr('stroke', 'none')
  }

  drawTooltip(e, d) {
    const vis = this
    const yValue = vis.countCyValue(d)
    const xValue = vis.x(d.date) // 🟢 Thêm dòng này để có xValue

    vis.tooltipLine1
      .attr('x1', xValue)
      .attr('y1', 0)
      .attr('x2', xValue)
      .attr('y2', vis.HEIGHT)
      .attr('stroke', '#1B3963')
      .attr('stroke-dasharray', '2, 2')
      .attr('stroke-width', '1px')
      .attr('opacity', 0.3)

    vis.tooltipLine2
      .attr('x1', 0)
      .attr('y1', yValue)
      .attr('x2', vis.WIDTH)
      .attr('y2', yValue)
      .attr('stroke', '#1B3963')
      .attr('stroke-dasharray', '2, 2')
      .attr('stroke-width', '1px')
      .attr('opacity', 0.3)

    vis.tip.show(e, d)

    requestAnimationFrame(() => {
      const tipNode = document.querySelector('.d3-tip-focus-chart')
      if (tipNode) {
        const bbox = vis.svg.node().getBoundingClientRect()
        const svgLeft = bbox.left + window.scrollX
        const svgTop = bbox.top + window.scrollY

        const tipWidth = tipNode.offsetWidth || 160
        const tipHeight = tipNode.offsetHeight || 40

        const padding = 8 // khoảng cách giữa dot và tooltip
        const offsetAboveDot = 12 // tooltip cao hơn dot một chút

        const tooltipLeft = svgLeft + xValue - tipWidth - padding
        const tooltipRight = svgLeft + xValue + padding
        const margin = 8

        const canShowLeft = tooltipLeft > margin

        tipNode.style.position = 'absolute'
        tipNode.style.left = canShowLeft
          ? `${tooltipLeft}px`
          : `${tooltipRight}px`

        const offsetBelowDot = 8
        if (isMobile) {
          const centeredTop = svgTop + yValue - tipHeight / 2
          const top = Math.max(margin, centeredTop)
          tipNode.style.top = `${top}px`
        } else {
          tipNode.style.top = `${svgTop + yValue + offsetBelowDot}px`
        }

        // copy
        const copyBtn = document.querySelector('.copy-btn')
        const copyThumbIcon = document.querySelector('.copy-thumb')
        const checkedCopyIcon = document.querySelector('.checked-copy')

        if (copyBtn && copyThumbIcon && checkedCopyIcon) {
          copyBtn.onclick = () => {
            const text = copyBtn.getAttribute('data-content')

            const showCheckedIcon = () => {
              copyThumbIcon.style.display = 'none'
              checkedCopyIcon.style.display = 'inline'
              setTimeout(() => {
                copyThumbIcon.style.display = 'inline'
                checkedCopyIcon.style.display = 'none'
              }, 1500)
            }

            if (
              navigator.clipboard &&
              typeof navigator.clipboard.writeText === 'function' &&
              window.isSecureContext
            ) {
              navigator.clipboard.writeText(text).then(() => {
                showCheckedIcon()
              })
            } else {
              const textArea = document.createElement('textarea')
              textArea.value = text
              textArea.style.position = 'fixed'
              textArea.style.left = '-9999px'
              textArea.style.top = '-9999px'
              document.body.appendChild(textArea)
              textArea.focus()
              textArea.select()
              try {
                document.execCommand('copy')
                showCheckedIcon()
              } catch (err) {
                console.error('Fallback copy failed', err)
              }
              document.body.removeChild(textArea)
            }
          }
        }

        //  tooltip move
        tipNode.addEventListener('mouseenter', () => {
          clearTimeout(this.tooltipTimeout)
        })

        tipNode.addEventListener('mouseleave', () => {
          this.tooltipTimeout = setTimeout(() => {
            vis.tip.hide()
            vis.removeTooltip()
          }, 500)
        })
      }
    })
  }

  addEventCircles() {
    const vis = this
    vis.g.selectAll('.event').remove()

    let listEventData = vis.events

    if (!vis.marketContextToggleObj?.showNoteHistory) {
      listEventData = vis.events.filter(d =>
        vis.marketContextToggleObj.showRiskEvents
          ? vis.isRiskEvent(d)
          : vis.isRiskStatus(d),
      )
    }

    if (
      !vis.marketContextToggleObj?.showNoteHistory &&
      vis.marketContextToggleObj.showUnrecognisedRiskStatus
    ) {
      listEventData = vis.filterDataByToggle(
        listEventData,
        vis.marketContextToggleObj,
      )
    }

    vis.events.length > 0 &&
      vis.g
        .selectAll('.event')
        .data(listEventData, d => d.id)
        .join(
          enter =>
            enter
              .append('circle')
              .attr('class', 'event')
              .attr('clip-path', `url(#${vis.clipId})`)
              .attr('cx', d => vis.x(d.date))
              .attr('cy', d => vis.countCyValue(d))
              .attr('r', '5')
              .attr('fill', d => vis.getEventColor(d))
              .style('stroke', 'white')
              .style('stroke-width', '1px')
              .style('opacity', d =>
                vis.filterByMarketContextToggle(d) ? 1 : 0,
              )
              .on('mouseover', (e, d) => {
                clearTimeout(this.tooltipTimeout)
                vis.drawTooltip(e, d)
              })
              .on('mouseout', () => {
                vis.tooltipTimeout = setTimeout(() => {
                  vis.tip.hide()
                  vis.removeTooltip()
                }, 1000)
              }),
          update => {
            update.attr('clip-path', `url(#${vis.clipId})`)
            update.attr('cx', d => vis.x(d.date))
            update.attr('cy', d => vis.countCyValue(d))
          },
          exit => exit.remove(),
        )
  }

  zoomed = ({ transform }) => {
    const vis = this
    vis.x.range([0, vis.WIDTH].map(d => transform?.applyX(d)))
    vis.xAxisCall.scale(vis.x)

    // current bounds of visualization
    const minDate = vis.x.invert(0)
    const maxDate = vis.x.invert(vis.WIDTH)

    const filteredData = vis.data.filter(
      d => d.date > minDate && d.date < maxDate,
    )
    vis.y.domain([
      d3.min(filteredData, d => Number(d.Price)) / 1.05,
      d3.max(filteredData, d => Number(d.Price)) * 1.05,
    ])
    vis.yAxisCall.scale(vis.y)

    if (d3.min(d3.map(filteredData, d => d.Price))) {
      vis.minText.text(
        `Min: ${vis?.currency} ${d3.min(d3.map(filteredData, d => d.Price))}`,
      )
    } else {
      vis.minText.text(null)
    }

    if (d3.max(d3.map(filteredData, d => d.Price))) {
      vis.maxText.text(
        `Max: ${vis?.currency} ${d3.max(d3.map(filteredData, d => d.Price))}`,
      )
    } else {
      vis.maxText.text(null)
    }

    vis.update({
      data: vis.data,
      events: vis.events,
      onZoom: true,
    })
  }

  brushed = ({ sourceEvent, selection, target }) => {
    if (!sourceEvent || sourceEvent.type === 'zoom') return // ignore brush-by-zoom
    const vis = this
    const s = selection || [0, vis.WIDTH]
    vis.svg
      .select('.zoom')
      .call(
        vis.zoom.transform,
        d3.zoomIdentity.scale(vis.WIDTH / (s[1] - s[0])).translate(-s[0], 0),
      )
  }

  toggleUpdate = ({ toggleProp }) => {
    const vis = this
    vis.marketContextToggleObj = toggleProp || {
      showRiskEvents: false,
      showUnrecognisedRiskStatus: true,
      showNoteHistory: false,
      showRiskOutlookWatchlist: true,
      showRiskSmoke: true,
      showRiskFire: true,
    }
    vis.addEventCircles()
  }

  filterDataByToggle = (data, toggleObj) => {
    const enabledOutlooks = []

    if (toggleObj.showRiskOutlookWatchlist) enabledOutlooks.push('Watchlist')
    if (toggleObj.showRiskSmoke) enabledOutlooks.push('Smoke')
    if (toggleObj.showRiskFire) enabledOutlooks.push('Fire')

    // Lọc data
    return data.filter(item => enabledOutlooks.includes(item?.risk_outlook))
  }
}

export default FocusChart
